/**
 * TopNav Component
 * 
 * Global navigation bar for Pleasance.
 * Mobile-responsive with hamburger menu.
 */

import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, User as UserIcon, LogIn, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Icon from './Icon';
import ChatButton from './ChatButton';

function TopNav() {
    const { isAuthenticated, user, signOut } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const [hoveredPath, setHoveredPath] = useState(null);
    const navItems = [
        { path: '/', label: 'Home' },
        { path: '/search', LucideIcon: Search, iconOnly: true, label: 'Search' },
    ];

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-[#0A0A0A] to-charcoal border-b border-wine-700/25 shadow-xl shadow-black/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link
                        to="/"
                        className="flex items-center gap-2 flex-shrink-0 group"
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        <span className="font-display flex items-baseline gap-1">
                            <span className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-wine-400 to-wine-500 bg-clip-text text-transparent group-hover:from-[#C4A35A] group-hover:to-wine-400 transition-all duration-500">
                                Pleasance
                            </span>
                            <span className="text-wine-600/70 text-sm sm:text-base font-medium">.app</span>
                        </span>
                        <span className="hidden lg:flex items-center h-4 ml-4 pl-4 border-l border-wine-700/40 text-[10px] font-bold uppercase tracking-[0.2em] text-wine-500/60 leading-none">
                            Play Without Shame
                        </span>
                    </Link>

                    {/* Desktop Nav */}
                    <div className="hidden md:flex items-center gap-1 flex-nowrap">
                        {/* Home Link */}
                        <Link
                            to="/"
                            onMouseEnter={() => setHoveredPath('/')}
                            onMouseLeave={() => setHoveredPath(null)}
                            className={`
                                relative px-3 py-2 text-sm font-medium transition-colors duration-200
                                flex items-center gap-2 z-10 whitespace-nowrap
                                ${isActive('/') ? 'text-wine-400' : 'text-gray-400 hover:text-gray-200'}
                            `}
                        >
                            {isActive('/') && (
                                <motion.div
                                    layoutId="navbar-active"
                                    className="absolute inset-0 bg-wine-500/10 -z-10"
                                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-10">Home</span>
                        </Link>

                        {/* Browse Link */}
                        <Link
                            to="/db/kinks"
                            onMouseEnter={() => setHoveredPath('/db')}
                            onMouseLeave={() => setHoveredPath(null)}
                            className={`
                                relative px-3 py-2 text-sm font-medium transition-colors duration-200
                                flex items-center gap-2 z-10 whitespace-nowrap
                                ${location.pathname.startsWith('/db') || location.pathname.startsWith('/browse') ? 'text-wine-400' : 'text-gray-400 hover:text-gray-200'}
                            `}
                        >
                            {(location.pathname.startsWith('/db') || location.pathname.startsWith('/browse')) && (
                                <motion.div
                                    layoutId="navbar-active"
                                    className="absolute inset-0 bg-wine-500/10 -z-10"
                                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-10">Browse</span>
                        </Link>

                        {/* Other Nav Items */}
                        {navItems.filter(item => item.path !== '/').map((item) => {
                            const active = isActive(item.path);

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onMouseEnter={() => setHoveredPath(item.path)}
                                    onMouseLeave={() => setHoveredPath(null)}
                                    className={`
                                        relative px-3 py-2 text-sm font-medium transition-colors duration-200
                                        flex items-center gap-2 z-10 whitespace-nowrap
                                        ${active ? 'text-wine-400' : 'text-gray-400 hover:text-gray-200'}
                                        ${item.badge ? 'opacity-60 cursor-not-allowed' : ''}
                                    `}
                                    onClick={(e) => item.badge && e.preventDefault()}
                                >
                                    {active && (
                                        <motion.div
                                            layoutId="navbar-active"
                                            className="absolute inset-0 bg-wine-500/10 -z-10"
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}

                                    {item.icon && <Icon name={item.icon} size={16} />}
                                    {item.LucideIcon && <item.LucideIcon size={16} />}
                                    {!item.iconOnly && <span className="relative z-10">{item.label}</span>}
                                    {item.badge && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-wine-500/20 text-wine-400 font-bold uppercase">
                                            {item.badge}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                        {/* Auth Link (Desktop) */}
                        <Link
                            to={isAuthenticated ? "/profile" : "/login"}
                            onMouseEnter={() => setHoveredPath(isAuthenticated ? "/profile" : "/login")}
                            onMouseLeave={() => setHoveredPath(null)}
                            className={`
                                relative px-3 py-2 text-sm font-medium transition-colors duration-200
                                flex items-center gap-2 z-10 whitespace-nowrap
                                ${isActive(isAuthenticated ? "/profile" : "/login") ? 'text-wine-400' : 'text-gray-400 hover:text-gray-200'}
                            `}
                        >
                            {isActive(isAuthenticated ? "/profile" : "/login") && (
                                <motion.div
                                    layoutId="navbar-active"
                                    className="absolute inset-0 bg-wine-500/10 -z-10"
                                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            {isAuthenticated ? <UserIcon size={16} /> : <LogIn size={16} />}
                            <span className="relative z-10">{isAuthenticated ? 'Profile' : 'Login'}</span>
                        </Link>

                        {/* Sign Out Button (Desktop) */}
                        {isAuthenticated && (
                            <button
                                onClick={signOut}
                                className="relative px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2 z-10 whitespace-nowrap text-gray-400 hover:text-red-400"
                            >
                                <LogOut size={16} />
                                <span>Sign Out</span>
                            </button>
                        )}

                        {/* AI Chat Button */}
                        <div className="ml-2">
                            <ChatButton />
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden p-2 text-gray-400 hover:text-gray-200 hover:bg-charcoal-950/50 transition-colors"
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? (
                            <Icon name="close" size={24} />
                        ) : (
                            <Icon name="menu" size={24} />
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="md:hidden border-t border-wine-900/20 bg-charcoal-950/95 backdrop-blur-md overflow-hidden"
                >
                    <div className="px-4 py-4 space-y-1">
                        {/* Home */}
                        <Link
                            to="/"
                            onClick={() => setMobileMenuOpen(false)}
                            className={`
                                block px-4 py-3 text-base font-medium transition-all
                                ${isActive('/') ? 'text-wine-400 bg-wine-500/10' : 'text-gray-400 hover:text-gray-200 hover:bg-charcoal-950'}
                            `}
                        >
                            Home
                        </Link>

                        {/* Browse Link */}
                        <Link
                            to="/db/kinks"
                            onClick={() => setMobileMenuOpen(false)}
                            className={`
                                block px-4 py-3 text-base font-medium transition-all
                                ${location.pathname.startsWith('/db') || location.pathname.startsWith('/browse') ? 'text-wine-400 bg-wine-500/10' : 'text-gray-400 hover:text-gray-200 hover:bg-charcoal-950'}
                            `}
                        >
                            Browse
                        </Link>

                        {/* Other Nav Items */}
                        {navItems.filter(item => item.path !== '/').map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => !item.badge && setMobileMenuOpen(false)}
                                className={`
                                    block px-4 py-3 text-base font-medium transition-all flex items-center justify-between
                                    ${isActive(item.path) ? 'text-wine-400 bg-wine-500/10' : 'text-gray-400 hover:text-gray-200 hover:bg-charcoal-950'}
                                    ${item.badge ? 'opacity-60' : ''}
                                `}
                            >
                                <span className="flex items-center gap-2">
                                    {item.LucideIcon && <item.LucideIcon size={20} />}
                                    {item.label}
                                </span>
                                {item.badge && (
                                    <span className="text-[10px] px-2 py-1 rounded bg-wine-500/20 text-wine-400 font-bold uppercase">
                                        {item.badge}
                                    </span>
                                )}
                            </Link>
                        ))}
                        {/* Auth Link (Mobile) */}
                        <Link
                            to={isAuthenticated ? "/profile" : "/login"}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`
                                block px-4 py-3 text-base font-medium transition-all flex items-center gap-2
                                ${isActive(isAuthenticated ? "/profile" : "/login") ? 'text-wine-400 bg-wine-500/10' : 'text-gray-400 hover:text-gray-200 hover:bg-charcoal-950'}
                            `}
                        >
                            {isAuthenticated ? <UserIcon size={20} /> : <LogIn size={20} />}
                            {isAuthenticated ? 'Profile' : 'Login'}
                        </Link>

                        {/* Sign Out Button (Mobile) */}
                        {isAuthenticated && (
                            <button
                                onClick={() => {
                                    signOut();
                                    setMobileMenuOpen(false);
                                }}
                                className="w-full block px-4 py-3 text-base font-medium transition-all flex items-center gap-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                            >
                                <LogOut size={20} />
                                Sign Out
                            </button>
                        )}
                    </div>
                </motion.div>
            )}
        </nav>
    );
}

export default TopNav;
